import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  Box,
  Checkbox,
  CircularProgress,
  Typography,
  useTheme,
} from '@monorepo/nova-ui';
import { AffiliateUserType } from '@monorepo/types';
import { useRegisterAffiliateModal } from './hooks';
import {
  StyledApplyButton,
  StyledButtonsContainer,
  StyledCancelButton,
  StyledCloseButton,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
  StyledErrorMessage,
  StyledErrorTitle,
  StyledFormControlLabel,
  StyledStateContainer,
  StyledTermsContent,
  StyledTermsList,
  StyledTermsListItem,
} from './styled';

export type AffiliationRegisterFormValues = {
  socialMediaLinks: { link: string }[];
  type: AffiliateUserType;
  message: string;
  acceptedTermsAndConditions: boolean;
};

interface RegisterAffiliateModalProps {
  open: boolean;
  onClose: () => void;
}

export const RegisterAffiliateModal: FC<RegisterAffiliateModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation(['affiliate', 'common']);
  const { palette, spacing } = useTheme();
  const {
    form,
    acceptedTermsAndConditions,
    rules,
    isLoading,
    isLoadError,
    onSubmit,
    handleClose,
  } = useRegisterAffiliateModal(onClose);

  const renderLoadingState = () => (
    <StyledStateContainer>
      <CircularProgress size={36} />
      <Typography variant="body1">{t('common:loading')}</Typography>
    </StyledStateContainer>
  );

  const renderErrorState = () => (
    <StyledStateContainer>
      <StyledErrorTitle>
        {t('affiliate:registration_modal_error.registration_failed')}
      </StyledErrorTitle>
      <StyledErrorMessage>
        {t('affiliate:registration_modal_error.something_went_wrong')}
      </StyledErrorMessage>
    </StyledStateContainer>
  );

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <StyledCloseButton onClick={handleClose} aria-label={t('common:close')}>
        <SystemIcon iconName="X" />
      </StyledCloseButton>
      <StyledDialogTitle>
        {t('affiliate:register_modal.title')}
      </StyledDialogTitle>
      <StyledDialogContent>
        {isLoading ? (
          renderLoadingState()
        ) : isLoadError ? (
          renderErrorState()
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <StyledTermsContent>
              <StyledTermsList>
                {rules.map((rule, idx) => (
                  <StyledTermsListItem key={idx}>
                    <Typography variant="body2">{rule}</Typography>
                  </StyledTermsListItem>
                ))}
              </StyledTermsList>
            </StyledTermsContent>
            <Controller
              name="acceptedTermsAndConditions"
              control={form.control}
              rules={{
                required: t('affiliate:register_modal.terms_required'),
              }}
              render={({ field, fieldState }) => (
                <Box>
                  <StyledFormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t('affiliate:register_modal.agree')}
                  />
                  {fieldState.error && (
                    <Typography
                      variant="caption"
                      component="p"
                      color={palette.error.main}
                      marginTop={spacing(1.5)}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
            <StyledButtonsContainer>
              <StyledCancelButton
                variant="naked"
                onClick={handleClose}
                disabled={isLoading}
              >
                {t('common:cancel')}
              </StyledCancelButton>
              <StyledApplyButton
                variant="primary"
                type="submit"
                disabled={!acceptedTermsAndConditions || isLoading}
              >
                {t('common:apply')}
              </StyledApplyButton>
            </StyledButtonsContainer>
          </form>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};
